import { useAppDispatch, useAppSelector } from "Redux/hooks";
import {
  addAlways,
  addOperator,
  askToDelete,
  negateTrigger,
} from "feature/producer/actions";
import { Module, Trigger, TriggerBody } from "types/type.module";
import { Direction } from "types/types";
import { extractLogicalOperation } from "../../Logic/Steps";
import Button from "../Atoms/Button";
import { resolveElementLabel } from "../Atoms/ElementLabel";
import ProducerTrigger from "../Atoms/ProduceTrigger";
import ProducerCell from "../Atoms/ProducerCell";
import ProducerTriggerAdd from "../Atoms/ProducerTriggerAdd";
import { selectCurrentModule } from "Redux/ProducerSlice";

/*
  @function ConditionComposer
  compose and visualize condition logic for step
  @params state {object} - state of condition
  @returns {React.Node}
*/
export default function ConditionComposer({
  condition,
  chapter,
  step,
}: {
  chapter: number;
  step: number;
  condition: Trigger;
}) {
  const dispatch = useAppDispatch();
  const currentModule = useAppSelector(selectCurrentModule);

  if (!currentModule) return null;

  const always = () => {
    if (typeof condition !== "string" && "and" in condition && condition.and) {
      if (condition.and.length > 0) {
        if (
          condition.and[0] &&
          typeof condition.and[0] !== "string" &&
          "or" in condition.and[0] &&
          condition.and[0].or &&
          condition.and[0].or.length === 0
        ) {
          return (
            <Button
              label="Always"
              onClick={() =>
                dispatch(addAlways({ step: step, chapter: chapter }))
              }
            />
          );
        }
      }
    }
  };

  return (
    <div className="overflow-x-auto">
      {always()}
      <Condition
        condition={condition}
        direction={[]}
        step={step}
        chapter={chapter}
        module={currentModule}
      />
    </div>
  );
}

function Condition({
  condition,
  direction,
  step,
  chapter,
  module,
}: {
  condition: Trigger | TriggerBody;
  direction: Direction[];
  step: number;
  chapter: number;
  module: Module;
}) {
  const dispatch = useAppDispatch();
  const operation = extractLogicalOperation(condition);

  if (operation.operation === "or") {
    return (
      <div
        key={(Math.random() + 1).toString(36).substring(7)}
        className="flex flex-col w-full"
      >
        <ProducerCell
          direction="row"
          level={direction.length}
          onDeletion={() =>
            dispatch(
              askToDelete({
                element: "trigger",
                directions: [...direction],
                chapter: chapter,
                step: step,
              })
            )
          }
        >
          {operation.elements &&
            operation.elements.map((el: any, i: any) => {
              if (el === "always") {
                return (
                  <div key={i}>
                    <ProducerTrigger
                      label="always"
                      onDeletion={() => {
                        dispatch(
                          askToDelete({
                            element: "trigger",
                            directions: [...direction, { op: "or", pos: i }],
                            chapter: chapter,
                            step: step,
                          })
                        );
                      }}
                    />
                  </div>
                );
              }
              if ("chapter" in el) {
                if (direction.length < 2) {
                  if (direction.length < 1) {
                    return Condition({
                      condition: { and: [{ or: [el] }] },
                      direction: [...direction, { op: "or", pos: i }],
                      step,
                      chapter,
                      module,
                    });
                  }
                  return Condition({
                    condition: { and: [el] },
                    direction: [...direction, { op: "or", pos: i }],
                    step,
                    chapter,
                    module,
                  });
                }
                return (
                  <div key={i} className="flex flex-row">
                    <ProducerTrigger
                      key={i}
                      label={resolveElementLabel(el, module)}
                      negated={el.negated}
                      onToggle={() =>
                        dispatch(
                          negateTrigger({
                            directions: [...direction, { op: "or", pos: i }],
                            chapter: chapter,
                            step: step,
                          })
                        )
                      }
                      onDeletion={() =>
                        dispatch(
                          askToDelete({
                            element: "trigger",
                            directions: [...direction, { op: "or", pos: i }],
                            chapter: chapter,
                            step: step,
                          })
                        )
                      }
                    />
                  </div>
                );
              }
              return Condition({
                condition: el,
                direction: [...direction, { op: "or", pos: i }],
                step,
                chapter,
                module,
              });
            })}

          {/* TODO: Why does this use "test" values in trigger? */}
          <ProducerTriggerAdd
            onClick={() => {
              dispatch(
                addOperator({
                  directions: [
                    ...direction,
                    { op: "or", pos: operation.elements.length },
                  ],
                  trigger: {
                    chapter: "test",
                    question: "test",
                    answer: "test",
                  },
                  chapter: chapter,
                  step: step,
                  operator: "and",
                })
              );
            }}
            operation="or"
          />
        </ProducerCell>
        {direction.length === 0 && (
          <ProducerTriggerAdd
            onClick={() => {
              dispatch(
                addOperator({
                  directions: [...direction],
                  operator: "and",
                  chapter: chapter,
                  step: step,
                  trigger: "5.5.5",
                })
              );
            }}
            operation="and"
          />
        )}
      </div>
    );
  }
  if (operation.operation === "and") {
    return (
      <div
        key={(Math.random() + 1).toString(36).substring(7)}
        className="flex flex-row w-full"
      >
        <ProducerCell
          direction="col"
          level={direction.length}
          onDeletion={() =>
            dispatch(
              askToDelete({
                element: "trigger",
                directions: [...direction],
                chapter: chapter,
                step: step,
              })
            )
          }
        >
          {operation.elements &&
            operation.elements.map((el: any, i: any) => {
              if (typeof el !== "string" && "chapter" in el) {
                if (direction.length < 2) {
                  if (direction.length < 1) {
                    return Condition({
                      condition: { or: [{ and: [el] }] },
                      direction: [...direction, { op: "and", pos: i }],
                      step,
                      chapter,
                      module,
                    });
                  }
                  return Condition({
                    condition: { or: [el] },
                    direction: [...direction, { op: "and", pos: i }],
                    step,
                    chapter,
                    module,
                  });
                }
                return (
                  <div key={i}>
                    <ProducerTrigger
                      key={i}
                      label={resolveElementLabel(el, module)}
                      negated={el.negated}
                      onToggle={() =>
                        dispatch(
                          negateTrigger({
                            directions: [...direction, { op: "and", pos: i }],
                            chapter: chapter,
                            step: step,
                          })
                        )
                      }
                      onDeletion={() =>
                        dispatch(
                          askToDelete({
                            element: "trigger",
                            directions: [...direction, { op: "and", pos: i }],
                            chapter: chapter,
                            step: step,
                          })
                        )
                      }
                    />
                  </div>
                );
              }
              return Condition({
                condition: el,
                direction: [...direction, { op: "and", pos: i }],
                step,
                chapter,
                module,
              });
            })}
          {/* TODO: Why does this use "test" values in trigger? */}
          <ProducerTriggerAdd
            onClick={() => {
              dispatch(
                addOperator({
                  directions: [
                    ...direction,
                    { op: "and", pos: operation.elements?.length },
                  ],
                  trigger: {
                    chapter: "test",
                    question: "test",
                    answer: "test",
                  },
                  chapter: chapter,
                  step: step,
                  operator: "or",
                })
              );
            }}
            operation="and"
          />
        </ProducerCell>
        {direction.length === 0 && (
          <ProducerTriggerAdd
            onClick={() => {
              dispatch(
                addOperator({
                  directions: [...direction],
                  operator: "or",
                  chapter: chapter,
                  step: step,
                  trigger: "5.5.5",
                })
              );
            }}
            operation="or"
          />
        )}
      </div>
    );
  }

  return null;
}
